// Core domain data types used across app and API

export interface BaseModel {
	id: string;
	createdAt: string;
	updatedAt: string;
	deletedAt?: string;
}

export enum WorkOrderStatus {
	Quote = 'quote',
	Open = 'open',
	InProgress = 'in_progress',
	PendingMaterials = 'pending_materials',
	PendingVendor = 'pending_vendor',
	Completed = 'completed',
	Cancelled = 'cancelled',
}

export enum WorkOrderPriority {
	Low = 'low',
	Medium = 'medium',
	High = 'high',
	Urgent = 'urgent',
}

export enum WorkOrderRecurrencePeriod {
	Days = 'days',
	Weeks = 'weeks',
	Months = 'months',
}

export interface WorkOrder extends BaseModel {
	orderNumber?: number;
	workRequested: string;
	workCompleted?: string;
	jobLocation?: string;
	terms?: string;
	status: WorkOrderStatus;
	priority: WorkOrderPriority;
	dueDate?: number;
	assignedTo?: string;
	associationId: string;
	createdBy: string;
	approvedBy?: string;
	pmApproved?: number;
	recurrencePeriod?: WorkOrderRecurrencePeriod | null;
	recurrenceValue?: number | null;
	// legacy (kept for backward compatibility)
	recurrenceIntervalMonths?: number | null;
	recurrenceParentId?: string;
	// Additional fields not in original schema but used in the app
	ownerName?: string;
	ownerPhone?: string;
	ownerEmail?: string;
	completedAt?: string;
	estimatedCost?: number;
	actualCost?: number;
	completionLat?: number;
	completionLng?: number;
	// Location properties
	latitude?: number;
	longitude?: number;
	savedToServerAt?: number;
	snoozeUntil?: string | null;
	// Set when the work order's association is archived (#269); NULL/undefined = active.
	archivedAt?: number | null;
	// Maintenance can flag a work order as needing PM follow-up, with a note (#48).
	followUpNeeded?: boolean;
	followUpNote?: string;

	/**
	 * The customer-facing rewrite of `workCompleted`, authored by accounting (#496).
	 *
	 * `workCompleted` is the INTERNAL record of what happened; this is what the PM, the
	 * printed work order and the invoice memo show. For every role except Maintenance,
	 * Accounting and Admin the API sends this and omits `workCompleted` entirely — a
	 * field that isn't in the payload cannot leak into a render.
	 */
	customerDescription?: string;
	/** Set when accounting finished its review pass; the WO is now the PM's to approve. */
	reviewApprovedAt?: number | null;
	/** Who marked it ready for approval. */
	reviewedBy?: string;
	/** Set when a PM sent it back for rework; clears `reviewApprovedAt`. */
	reviewReturnedAt?: number | null;
	/** Why the PM sent it back. Required on the return path. */
	reviewReturnNote?: string;
}

// Extended interface for form data that includes related objects
export interface WorkOrderFormData extends Omit<WorkOrder, 'assignedTo' | 'associationId' | 'createdAt' | 'completedAt' | 'latitude' | 'longitude'> {
	assignedTo?: User | null;
	association?: Association | null;
	savedToServerAt?: number;
	latitude?: number | null;
	longitude?: number | null;
	createdAt?: Date | string;
	completedAt?: number | string;
}

export enum AssociationType {
	HOA = 'HOA',
	Condo = 'condo',
}

export enum AssociationStatus {
	Active = 'active',
	Inactive = 'inactive',
	Archived = 'archived',
}

export interface Association extends BaseModel {
	name: string;
	address?: string;
	city?: string;
	state?: string;
	zipCode?: string;
	zip?: string; // Alias for zipCode
	type: AssociationType;
	status: AssociationStatus;
	lat?: number;
	lng?: number;
	// Additional properties used in the app
	latitude?: number; // Alias for lat
	longitude?: number; // Alias for lng
	contactName?: string;
	contactEmail?: string;
	contactPhone?: string;
	notes?: string;
	assignedTo?: string;
	archivedAt?: number;
	numberOfUnits?: number;
	workOrderCount?: number;
	collectionsFirstNoticeTemplate?: string;
	collectionsSecondNoticeTemplate?: string;
	collectionsIntentToLienTemplate?: string;
	/** Printed copy overrides; `null` = none saved (use the default). */
	statementLegalText?: string | null;
	statementCalloutText?: string | null;
	/** Invoice-only overrides (#678) — the invoice slip falls back to the statement copy. */
	invoiceLegalText?: string | null;
	invoiceCalloutText?: string | null;
	/** Free-text name of the master association this one rolls up to (#302).
	 *  Used for flagging; shown on association cards. */
	masterAssociation?: string;
	/** Federal EIN, stored as `sf1:iv:ct` client-side ciphertext (Secure Fields).
	 *  May be legacy plaintext on never-re-saved rows. */
	ein?: string;
	/** Plaintext display mask for `ein` (e.g. `***-**-1234`), computed client-side. */
	einMask?: string;
	accountsReceivableGlCodeId?: string;
	accountsPayableGlCodeId?: string;
	/** When true, each dues level may carry its own GL split (one set of
	 *  association_dues_lines per level). When false (legacy), the default
	 *  lines are pro-rated to each level's amount. Also enables par-value-as-
	 *  percent-of-budget dues when `operatingBudgetCents` is set (#232). */
	parValue?: boolean;
	/** Annual operating budget in cents. With `parValue` on, an account's dues
	 *  are budget × (account.parValueBps ÷ 10000) ÷ periods-per-year (#232). */
	operatingBudgetCents?: number;
	/** Default accounting basis applied to reports that expose the cash/accrual
	 *  chip (Balance Sheet, P&L, Trial Balance, GL Report). Undefined / null
	 *  means "no preference" — reports fall back to the app-wide accrual default.
	 *  Null is also the wire shape PUT accepts to clear a previously-set default. */
	defaultAccountingBasis?: AccountingBasis | null;
	/** Explicit `<Bucket>/<Folder Name>` location of this association's documents on
	 *  Dropbox, overriding the name-derived default. Set by a super admin when the drive
	 *  folder is named something the derivation cannot reach — a short name kept from a
	 *  takeover, a pre-rename name, or a community filed under the wrong bucket. Case
	 *  differences need no override; those are folded. Null/undefined = derive.
	 *  See migration 247 and `associationFolderSegments`. */
	dropboxPath?: string | null;
	/** Dropbox's own id for that folder, recorded when the association was onboarded FROM
	 *  the folder (#523, migration 259). The path stays authoritative for resolution; the id
	 *  is what survives a rename — and SBS's `(initials)` suffix means reassigning a property
	 *  manager renames the folder. Null on every association created before or outside the
	 *  onboarding flow, which is a normal state, not a broken one. */
	dropboxFolderId?: string | null;
}

/** Reports that switch between cash and accrual presentation. The two values
 *  are the only strings the API/DB CHECK constraint accept. */
export type AccountingBasis = 'cash' | 'accrual';

/** Print disposition for invoices produced by an accounting cron run. */
export const PRINT_MODES = ['now', 'queue', 'none'] as const;
export type PrintMode = typeof PRINT_MODES[number];

/**
 * Tenant / management-company-level config. One row per `users.organization_id`;
 * statement headers and contact strips pull from here so the same PM company's
 * branding is consistent across every association it runs.
 */
export interface Organization {
	id: string;
	name?: string;
	address?: string;
	city?: string;
	state?: string;
	zip?: string;
	phone?: string;
	fax?: string;
	email?: string;
	website?: string;
	/** Multi-line remit-to address (often a PO Box) for the payment-instruction
	 *  box on printed statements. Distinct from `address` which is the office
	 *  address printed in the c/o header. */
	remitAddress?: string;
	/** Management-company logo as a base64 data URL ("data:image/png;base64,…").
	 *  Rendered on printed mailings (envelope top-left above the return address).
	 *  Stored inline so print pages need no authed fetch. */
	logo?: string;
	/** Brand color (hex, e.g. "#0d47a1") for the envelope return-address text so
	 *  it matches the logo. Print renderers default to dark blue when unset. */
	brandColor?: string;
	/** Markdown "cheat sheet" documenting how this org processes Exterior
	 *  Improvement Applications; org-editable, shown from the inbox header (#363). */
	exteriorCheatSheet?: string;
	/** The `vendors` row that IS this management company — "this vendor is us".
	 *  Vendors are global, so one row bills every association. Work-order billing
	 *  is disabled until this is set, and the cross-association invoice report is
	 *  simply this vendor's bills. Resolved by id, never by name. */
	selfVendorId?: string;
	/** Read-only: `selfVendorId` names a vendor that still exists. Deleting a vendor
	 *  only soft-deletes it and leaves the pointer behind; billing (`resolveSelfVendor`)
	 *  then treats the org as unconfigured, and so should anything linking to it. */
	selfVendorActive?: boolean;
	/** Prefix for minted invoice numbers, e.g. "SBS-" → bill number "SBS-1001".
	 *  Absent / empty means NO prefix — the bill number is the bare number. */
	selfInvoicePrefix?: string;
	/** The next invoice number this app will issue. STORED and settable (migration
	 *  249), not derived from MAX() — so the sequence can be steered to continue a
	 *  run that started in QuickBooks, skip a block, or fix a mistake. Advanced
	 *  atomically on every post, including when an override bypasses it. */
	nextInvoiceNumber?: number;
	/** Go-live instant for work-order billing (epoch ms). Work orders approved
	 *  BEFORE this never enter the Ready-to-Bill queue — that history was already
	 *  invoiced by hand, and offering it would invite billing an association
	 *  twice. Stamped automatically when a self vendor is first configured;
	 *  unset means the queue shows nothing (fails closed). */
	woBillingStartAt?: number;
	/** Portfolio-wide expense coding for generated work-order bill lines. An
	 *  association may override any of these; both fall back to unassigned
	 *  expense (9991) so a generated line is never left unattributed. */
	woLaborGlCodeId?: string;
	woMaterialsGlCodeId?: string;
	woMiscGlCodeId?: string;
	/** Go-live switch for AUTOMATIC outbound email — acknowledgments, new-message
	 *  notifications, the nightly digest. ANDed with `ENVIRONMENT === 'production'`
	 *  in `utils/server-email.ts` — deploying to production must not, by itself,
	 *  start mailing homeowners. Defaults OFF. Does NOT govern a send a user
	 *  explicitly composed; that is `manualEmailSendingEnabled`. */
	emailSendingEnabled?: boolean;
	/** Kill switch for MANUAL sends — Forward, the needs-info letter, anything a
	 *  signed-in user composed and pressed a button to send. Defaults ON, the
	 *  opposite of `emailSendingEnabled`: its failure mode is a person pressing
	 *  Send and being silently ignored rather than the app mailing people nobody
	 *  authorized. Still ANDed with `ENVIRONMENT === 'production'`. See
	 *  `EmailIntent` in `utils/server-email.ts` and migration 244. */
	manualEmailSendingEnabled?: boolean;
	/** Global switch for the recurring weekday DIGESTS — both the inbox digest (#412)
	 *  and the work-order approval digest (#496). Narrower than the two switches
	 *  above, which cannot silence the sweeps specifically; that matters because a
	 *  digest is the only mail that arrives when nobody did anything. Defaults ON,
	 *  because the inbox digest is already live and a pending migration must not
	 *  silently stop it. See migration 251. */
	digestEmailsEnabled?: boolean;
	/** The work-order approval digest's own switch (#496) — the daily reminder to
	 *  property managers plus the overdue escalation to accounting. Separate from
	 *  `digestEmailsEnabled` so going live with approvals does not mean touching the
	 *  inbox digest. Defaults OFF: new automatic mail must never begin flowing
	 *  merely because a deploy landed. Additionally scoped to managers on the v2
	 *  dashboard, so nobody is mailed about a queue their home screen omits. */
	woApprovalDigestEnabled?: boolean;

	// --- Secure Fields password ---
	// PBKDF2-SHA-256 salt (base64url, 16 bytes) used to derive the org-wide
	// key from the shared password. Persisted so every device derives the
	// same key from the same password.
	secureSalt?: string;
	// Verifier ciphertext (`sf1:iv:ct`) — a JSON payload pinned to this
	// org id, encrypted with the derived key. Decrypts iff the password is
	// correct. Backend never derives or verifies — pure storage.
	secureVerifier?: string;
	// PBKDF2 iteration count used when the verifier was created. Persisted
	// so the work factor can be raised later without breaking old verifiers.
	secureKdfIters?: number;
	// ISO timestamp of password setup. Informational only.
	secureSetupAt?: string;
	// Backend-derived `!!secure_verifier`. Lets the UI decide between
	// "Set password" and "Change/reset password" without parsing the blob.
	hasSecurePassword?: boolean;
}

export enum UserRole {
	Admin = 'admin',
	PropertyManager = 'property_manager',
	Maintenance = 'maintenance',
	Contractor = 'contractor',
	Accounting = 'accounting',
	Support = 'support',
}

export enum RateType {
	Standard = 'standard',
	Overtime = 'overtime',
	OnCall = 'on_call',
	Custom = 'custom',
}

export interface User extends BaseModel {
	email: string;
	name: string;
	role: UserRole;
	isAdmin: boolean;
	organizationId: string;
	hourlyRate?: number;
	overtimeRate?: number;
	onCallRate?: number;
	lastLat?: number;
	lastLng?: number;
	archivedAt?: string;
	/** Hex chart color (e.g. "#2196F3"), assigned on create and overridable. */
	color?: string;
	/**
	 * Optional signature image as an inline base64 data URL
	 * ("data:image/png;base64,..."). Rendered on printed work-order approval
	 * forms in place of the typed name. Users manage their own (#38).
	 */
	signatureImage?: string;
	/**
	 * Closing block for mail this user composes — substituted for `{{signature}}` in letters
	 * and canned replies. Free text so it can carry a title, phone or custom closing.
	 *
	 * Absent means "not set", NOT "no signature": the send sites fall back to
	 * "Thanks, <name>" via `resolveEmailSignature`, so this is deliberately the raw column
	 * rather than the resolved value.
	 */
	emailSignature?: string;
	/** Email me as soon as a new inbox item lands for one of my associations (#412). */
	notifyInboxEmail?: boolean;
	/** Include me in the weekday-morning digest of pending inbox items (#412). */
	notifyInboxDigest?: boolean;
	/** Email me the weekday summary of work orders waiting on MY approval. */
	notifyApprovalDigest?: boolean;
	/**
	 * Email me the portfolio-wide overdue-approval escalation.
	 *
	 * `null` means nobody has chosen, and the server resolves the default from role
	 * (accounting yes, everyone else no) — so this is deliberately NOT collapsed to
	 * `false`, or the settings screen could not show what will actually happen.
	 */
	notifyApprovalEscalation?: boolean | null;
}

export interface TimeEntry extends BaseModel {
	workOrderId: string;
	userId: string;
	startTime: string;
	endTime?: string;
	description?: string;
	hourlyRate?: number;
	rateType?: RateType;
	// Additional properties used in the app
	hours?: number;
	rate?: number;
	entryDate?: string;
	user?: User;
}

export interface Material extends BaseModel {
	name: string;
	description?: string;
	unit?: string;
	costPerUnit?: number;
	// Additional properties used in the app
	workOrderId?: string; // Link to owning work order
	materialCostId?: string | null; // Linked inventory item
	quantity?: number;
	unitCost?: number;
	unitType?: string;
	materialCost?: MaterialCost;
}

export interface MaterialCost extends BaseModel {
	workOrderId: string;
	materialId: string;
	quantity: number;
	costPerUnit: number;
	totalCost: number;
	description?: string;
	// Additional properties used in the app
	name?: string;
	defaultCost?: number;
	unitType?: string;
}

export interface MiscCost extends BaseModel {
	workOrderId: string;
	description: string;
	amount: number;
	// Additional properties used in the app
	quantity?: number;
	unitType?: string;
	cost?: number;
	totalCost?: number;
}

export interface Note extends BaseModel {
	workOrderId: string;
	userId: string;
	content: string;
	// Additional properties used in the app
	text?: string; // Alias for content
	createdBy?: string; // Alias for userId
	createdAtLat?: number;
	createdAtLong?: number;
}

export enum AttachmentType {
	BeforePhoto = 'before_photo',
	AfterPhoto = 'after_photo',
	Receipt = 'receipt',
	Invoice = 'invoice',
	Misc = 'misc',
	/**
	 * Website-form uploads (#369). Named after what the FORM called the field, not after
	 * what a work order would call it — an improvement request's "Literature 2" is the
	 * manufacturer's brochure the board has to read, and filing it as `misc` alongside a
	 * receipt loses exactly the distinction the reviewer needs.
	 */
	Photo = 'photo',
	Literature = 'literature',
	Quote = 'quote',
	Plan = 'plan',
	Insurance = 'insurance',
}

export interface Attachment extends BaseModel {
	workOrderId?: string;
	fileName: string;
	fileSize: number;
	fileType: string;
	description?: string;
	attachmentType: AttachmentType;
	inspectionReportId?: string;
	associationId?: string;
	billId?: string;
	invoiceId?: string;
	paymentId?: string;
	applicationId?: string;
	/** Request-type checklist item this file satisfies (exterior applications). */
	itemKey?: string | null;
	createdBy?: string; // user id of the uploader
	photoTakenAt?: string; // ISO string
	photoLat?: number;
	photoLng?: number;
	// Additional properties used in the app
	type?: string; // Alias for attachmentType
	name?: string; // Alias for fileName
	storageKey?: string;
	isImage?: boolean;
	isVideo?: boolean;
	isPdf?: boolean;
	uri?: string;
	mimeType?: string;
	size?: number; // Alias for fileSize
	width?: number;
	height?: number;
	imageWidth?: number;
	imageHeight?: number;
	imageSize?: { width: number; height: number };
	annotations?: AnnotationShape[];
}

export interface AttachmentAnnotation extends BaseModel {
	attachmentId: string;
	annotationType: 'rectangle' | 'arrow';
	x: number;
	y: number;
	width?: number; // For rectangles
	height?: number; // For rectangles
	color: string;
	strokeWidth: number;
	fillOpacity: number;
	label?: string;
	createdBy: string;
}

// Keep AnnotationShape for frontend compatibility
export interface AnnotationShape {
	id: string;
	type: 'rectangle' | 'arrow';
	coordinates: {
		x: number;
		y: number;
		width: number;
		height: number;
	};
	style: {
		color: string;
		strokeWidth: number;
		fillOpacity: number;
	};
	label?: string;
}

export enum LocationEventType {
	CheckIn = 'check_in',
	CheckOut = 'check_out',
	ManualCheckIn = 'manual_check_in',
	ManualCheckOut = 'manual_check_out',
}

export interface LocationEvent extends BaseModel {
	userId: string;
	workOrderId?: string;
	associationId?: string;
	type: LocationEventType;
	lat: number;
	lng: number;
	accuracy?: number;
	timestamp: string;
	// Additional properties used in the app
	eventType?: string; // Alias for type
	eventTimestamp?: number; // Alias for timestamp
	formattedDistance?: string;
	userLatitude?: number;
	userLongitude?: number;
	association?: Association;
	workOrder?: WorkOrder;
	/** Set on a check-in's create response when it filled the association's missing coordinates. */
	associationLocationBackfilled?: boolean;
}

export interface LocationAnalyticsEntry {
	id: string;
	userId: string;
	userName?: string;
	userEmail?: string;
	action: string;
	latitude?: number | null;
	longitude?: number | null;
	accuracy?: number | null;
	appVersion?: string | null;
	devicePlatform?: string | null;
	deviceOS?: string | null;
	deviceModel?: string | null;
	ipAddress?: string | null;
	userAgent?: string | null;
	createdAt: string;
	rawPayload?: Record<string, unknown> | null;
}

export interface InspectionReport extends BaseModel {
	associationId: string;
	userId: string;
	title: string;
	description?: string;
	scheduledDate?: string;
	completedAt?: string;
	// Additional properties used in the app
	reportNumber?: string;
}

export interface InspectionIssue extends BaseModel {
	title: string;
	description?: string;
	severity: 'low' | 'medium' | 'high' | 'critical';
	category: string;
	// Additional properties used in the app
	name?: string; // Alias for title
	isActive?: boolean;
	color?: string;
	iconName?: string;
	displayIcon?: string;
	displayColor?: string;
	severityColor?: string;
	severityDisplayName?: string;
	categoryDisplayName?: string;
}

export interface InspectionReportIssue extends BaseModel {
	inspectionReportId: string;
	inspectionIssueId: string;
	notes?: string;
	resolved: boolean;
	resolvedAt?: string;
}

export interface DeviceToken extends BaseModel {
	deviceToken: string;
	userId: string;
	deviceName?: string;
	devicePlatform?: string;
	userAgent?: string;
	ipAddress?: string;
	lastUsedAt: string;
	expiresAt?: string;
	expoPushToken?: string;
}

export enum InspectionIssueSeverity {
	Low = 'low',
	Medium = 'medium',
	High = 'high',
	Critical = 'critical',
}

export enum InspectionIssueCategory {
	Maintenance = 'maintenance',
	Safety = 'safety',
	Compliance = 'compliance',
	General = 'general',
	Plumbing = 'plumbing',
	Electrical = 'electrical',
	Structural = 'structural',
	HVAC = 'hvac',
	Exterior = 'exterior',
	Interior = 'interior',
}

export interface LocationData {
	latitude: number;
	longitude: number;
	accuracy?: number;
	timestamp: number;
	// Additional properties used in the app
	coords?: {
		latitude: number;
		longitude: number;
		accuracy?: number;
	};
}

export enum FeatureFlagName {
	TasksEnabled = 'tasks_enabled',
	Accounting = 'accounting',
	SqlAdmin = 'sql_admin',
	/** The unified message inbox (#369). Per-user so it can be rolled out gradually. */
	Inbox = 'inbox',

	/**
	 * The three grants below exist because Email Templates, the Exterior Improvement
	 * ruleset and the process cheat sheet all gated on the raw `isAdmin` flag, so the
	 * only way to let a support user edit any of them was to make them a full admin —
	 * which also hands over user management and the whole accounting surface.
	 *
	 * Per-user rather than a role in `api/permissions.ts` so they can be granted to one
	 * person without widening the Support role for everyone, and revoked without a deploy.
	 * Admins always pass regardless (see `isAdminOrFlagged`).
	 */

	/** Create/edit/delete the wording of outbound letter templates. */
	EmailTemplatesAdmin = 'email_templates_admin',
	/** Create/edit/delete the shared Exterior Improvement request types + their checklists. */
	ApplicationRulesAdmin = 'application_rules_admin',
	/** Edit the org's Exterior Improvement process cheat sheet (organizations.exterior_cheat_sheet). */
	CheatSheetAdmin = 'cheat_sheet_admin',

	/**
	 * User administration: create, update, delete and impersonate users (#500).
	 *
	 * The named replacement for the `is_admin` COLUMN, which is being retired. That column
	 * resolved its holder against the entire Admin matrix — 71 extra permissions for a
	 * property manager, 99 for a contractor — because "some unspecified amount of admin" has
	 * no other way to be interpreted. Naming the capability is the fix: this grants exactly
	 * `ELEVATED_PERMISSIONS` in api/permissions.ts and nothing else.
	 *
	 * `role = admin` remains separate and unaffected: that role bypasses every check outright,
	 * which is why a super administrator needs no flag at all.
	 */
	UserAdmin = 'user_admin',

	/**
	 * Association administration: create, edit and ARCHIVE associations.
	 *
	 * The second thing `is_admin` used to carry. Retiring that column (#500) kept only
	 * `user_admin`, so the two people who held it — a property manager and an accounting user —
	 * silently lost the association management they had been doing for months. This names it.
	 *
	 * Archiving is included because an association's archive IS an update: the PUT sets
	 * `archived_at` and cascades it to the association's work orders. Hard DELETE is
	 * deliberately excluded — that stays with the Admin role, since it is unrecoverable.
	 *
	 * Grants exactly `CAPABILITY_PERMISSIONS[association_admin]` in api/permissions.ts, and
	 * only to a role that already holds `VIEW_ASSOCIATIONS` (the floor).
	 */
	AssociationAdmin = 'association_admin',

	/**
	 * Work-order administration: DELETE a work order and its cost lines, delete materials-catalog
	 * items, and correct a work order's Date of Order.
	 *
	 * Also lost with `is_admin`, which is how its holders did all of these before #500. Every
	 * non-contractor role already creates and updates work orders from its own matrix row, so a
	 * capability for those would grant nothing. Only the Accounting role holds `DELETE_WORK_ORDERS`
	 * outright; this is how a property manager gets it by name instead of by magic column. The
	 * Date of Order unlock is not a permission — see `getNonUpdatableWorkOrderFields`.
	 *
	 * Floored on `UPDATE_WORK_ORDERS`: someone who cannot edit a work order has no business
	 * removing one. That also excludes contractors, whose row denies it.
	 */
	WorkOrderAdmin = 'work_order_admin',

	/**
	 * Accounting administration: the full accounting WRITE set, for someone whose role is
	 * not Accounting.
	 *
	 * The `accounting` flag only opens the area. Every non-Admin/non-Accounting role is then
	 * pinned to `ACCOUNTING_VIEWER_WITH_NOTES` — view everything, edit notes, write nothing —
	 * so there was no way to let a property manager who does the books actually save. The
	 * gap showed up as a property manager being handed the association edit page's Dues and
	 * Late-fees sections and getting `403 update_accounts` on save, because the FLAG decides
	 * what renders while the PERMISSION decides what persists.
	 *
	 * Grants exactly the inverse of that overlay — every permission the overlay denies —
	 * derived from it in api/permissions.ts so the two cannot drift. Floored on
	 * `VIEW_ACCOUNTS`, and useless without the `accounting` flag, since every accounting
	 * endpoint gates on that first.
	 */
	AccountingAdmin = 'accounting_admin',
}

export interface FeatureFlag extends BaseModel {
	userId: string;
	flagName: FeatureFlagName;
	enabled: boolean;
}

export enum TaskStatus {
	Open = 'open',
	InProgress = 'in_progress',
	Completed = 'completed',
	Cancelled = 'cancelled',
	Archived = 'archived',
}

export const TASK_COLORS = [
	{ label: 'Slate', value: '#64748b' },
	{ label: 'Red', value: '#ef4444' },
	{ label: 'Orange', value: '#f97316' },
	{ label: 'Amber', value: '#f59e0b' },
	{ label: 'Emerald', value: '#10b981' },
	{ label: 'Teal', value: '#14b8a6' },
	{ label: 'Blue', value: '#3b82f6' },
	{ label: 'Violet', value: '#8b5cf6' },
	{ label: 'Pink', value: '#ec4899' },
	{ label: 'Rose', value: '#f43f5e' },
] as const;

export enum RecurrenceType {
	None = 'none',
	Daily = 'daily',
	Weekly = 'weekly',
	Monthly = 'monthly',
	Yearly = 'yearly',
	Custom = 'custom',
}

export enum WeekDay {
	Sunday = 'sunday',
	Monday = 'monday',
	Tuesday = 'tuesday',
	Wednesday = 'wednesday',
	Thursday = 'thursday',
	Friday = 'friday',
	Saturday = 'saturday',
}

export enum MonthlyRecurrenceType {
	ByDate = 'by_date', // e.g., "15th of every month"
	ByDay = 'by_day', // e.g., "second Tuesday of every month"
}

export interface RecurrenceRule {
	type: RecurrenceType;
	interval: number; // e.g., every 2 weeks
	daysOfWeek?: WeekDay[]; // for weekly recurrence
	monthlyType?: MonthlyRecurrenceType; // for monthly recurrence
	dayOfMonth?: number; // for by-date monthly recurrence (1-31)
	weekOfMonth?: number; // for by-day monthly recurrence (1-5, where 5=last)
	dayOfWeekForMonth?: WeekDay; // for by-day monthly recurrence
	endDate?: number; // timestamp when recurrence ends
	maxOccurrences?: number; // maximum number of occurrences
}

export interface RecurringTask extends BaseModel {
	parentTaskId: string; // ID of the template task
	recurrenceRule: RecurrenceRule; // JSON string of the recurrence rule
	nextDueDate?: number; // When the next task should be created
	occurrenceCount: number; // How many times this has recurred
	isActive: boolean; // Whether this recurrence is still active
	createdBy: string;
}

export interface Task extends BaseModel {
	title: string;
	description?: string;
	status: TaskStatus;
	dueDate?: number;
	startTime?: number;
	endTime?: number;
	color?: string;
	assignedTo?: string;
	associationId?: string;
	taskList?: string;
	// Set when the task was auto-created from a work order flagged for follow-up (#48).
	workOrderId?: string;
	createdBy: string;
	isRecurring?: boolean;
	recurringTaskId?: string; // ID of the recurring task template
	parentTaskId?: string; // For individual occurrences of recurring tasks
}

/**
 * A work order flagged as needing PM follow-up, surfaced on the dashboard (#48).
 * Denormalized join of the auto-created follow-up task + its work order.
 */
export interface FollowUp {
	taskId: string;
	title: string;
	description?: string;
	status: string;
	assignedTo?: string | null;
	createdAt: string;
	workOrderId: string;
	orderNumber: number;
	jobLocation?: string | null;
	followUpNote?: string | null;
	associationId?: string | null;
	associationName?: string | null;
}

export interface CalendarSource {
	id: string;
	connectionId: string;
	externalId: string;
	name: string;
	color: string;
	isPrimary: boolean;
	isVisible: boolean;
	providerEmail: string;
	lastSyncedAt?: string;
	syncError?: string;
}

export interface CalendarEvent {
	id: string;
	sourceId: string;
	externalId: string;
	title: string;
	description?: string;
	location?: string;
	startTime: number;
	endTime: number;
	isAllDay: boolean;
	color?: string;
	htmlLink?: string;
	status: string;
	sourceName?: string;
}

export type CalendarItem = (Task & { _type: 'task' }) | (CalendarEvent & { _type: 'event'; dueDate: number });

export interface AssociationShortcutCode extends BaseModel {
	associationId: string;
	shortcutCode: string;
	fullAddress: string;
	city?: string;
	state?: string;
	zipCode?: string;
	latitude?: number;
	longitude?: number;
	createdBy: string;
	// Additional properties used in the app
	lat?: number; // Alias for latitude
	lng?: number; // Alias for longitude
	zip?: string; // Alias for zipCode
}

// ==========================================================================
// Accounting module
// ==========================================================================

/**
 * Whether a Person row describes a human or an entity (builder, trust, bank).
 * A `company` person keeps its legal name in `companyName` and leaves
 * `firstName`/`lastName` blank — `personDisplayName` keys off this rather than
 * guessing from which fields happen to be filled.
 */
export enum PersonType {
	Individual = 'individual',
	Company = 'company',
}

export interface Person extends BaseModel {
	/** Empty string for a `company` person — the column is NOT NULL and the table
	 *  can't be rebuilt (inbound FKs), so '' is the stored "no first name". */
	firstName: string;
	lastName?: string;
	/** Undefined on every row predating #424 — treat as `PersonType.Individual`. */
	personType?: PersonType;
	email?: string;
	phone?: string;
	notes?: string;
	userId?: string;
	createdBy?: string;
	importBatchId?: string;
	/** Source-system primary key (e.g. QB CUST.REFNUM). */
	externalId?: string;
	/** Optional legal/company/trust name that prints BEFORE the person's name
	 *  on envelopes and statements (#227). */
	companyName?: string;
	/** Optional single emergency contact (#226). */
	emergencyContactName?: string;
	emergencyContactRelationship?: string;
	emergencyContactPhone?: string;
	emergencyContactEmail?: string;
}

/**
 * User-curated vocabulary of board/committee positions a Person can hold
 * (Trustee, President, Treasurer, …). Stored in the `person_positions`
 * table so property managers can add their own entries from the
 * Edit-person modal without a code change. See migration 142.
 */
export interface PersonPosition extends BaseModel {
	name: string;
	sortOrder: number;
}

/**
 * Term of service for one person in one position. Multiple rows per
 * person are expected — past terms (with a closed end date) AND
 * concurrent positions both live here. `endDate == undefined` means the
 * assignment is currently active. See migration 143.
 */
export interface PersonPositionAssignment extends BaseModel {
	personId: string;
	positionId: string;
	/** Position display name, joined in for list views so callers don't N+1. */
	positionName?: string;
	/** Unix-ms midnight of the term start. */
	startDate?: number;
	/** Unix-ms midnight of the term end; undefined while active. */
	endDate?: number;
	notes?: string;
}

export enum AccountStatus {
	Active = 'active',
	Closed = 'closed',
	Suspended = 'suspended',
}

/**
 * Editable account tag (migration 170). DB-backed registry that supersedes the
 * hardcoded ACCOUNT_FLAG_* constants below — those now serve only as the
 * migration seed + a render fallback for codes missing from the registry.
 * Behavior lives on the row: `pausesBilling` skips auto dues posting,
 * `suppressesLateFees` skips auto late fees, `triggersLegalAdminFees` adds a
 * Legal Admin fee alongside a late fee (#244).
 */
export interface AccountTag extends BaseModel {
	code: string;
	label: string;
	description?: string;
	color?: string;
	pausesBilling: boolean;
	suppressesLateFees: boolean;
	triggersLegalAdminFees: boolean;
	sortOrder: number;
	createdBy?: string;
}

/**
 * Account flags — issue #170. Multi-valued advisory tags attached to an
 * account. Some flags have downstream business rules (Pre-Ch13 halts auto
 * billing + triggers an account split into a Post-Ch13 sibling; PP exempts
 * the account from automatic late-fee posting). Wiring those side effects
 * lives outside this enum — see the cron / account-split follow-ups.
 */
export const ACCOUNT_FLAG_CODES = [
	'DL',         // Demand Letter
	'L',          // Lien
	'F-Assoc',    // Foreclosure by Association
	'F-Bank',     // Foreclosure by Bank
	'SS',         // Sheriff Sale
	'Pre-Ch13',   // Pre Chapter 13 — pauses auto-billing; splits account
	'Post-Ch13',  // Post Chapter 13 — created on Pre-Ch13 split
	'R',          // Renter
	'PP',         // Payment Plan — suppresses automatic late fees
	'X',      // Pause Payments — suppresses ALL automatic billing (dues + late fees)
	'ACH',        // ACH Payment — owner pays by ACH/autopay (QB backtick "`" marker)
] as const;
export type AccountFlag = typeof ACCOUNT_FLAG_CODES[number];

/**
 * Flags that PAUSE automatic dues posting. The dues cron skips these accounts;
 * the Post Dues preview still lists them (unchecked) so a PM can force one on
 * as a Manual Override. Add a flag's code here to make "pause payments if this
 * flag is set" true for it.
 */
export const BILLING_PAUSE_FLAG_CODES: readonly AccountFlag[] = ['Pre-Ch13', 'X'];

/**
 * Flags that suppress automatic LATE FEES. Superset of the dues-pause flags
 * (a paused account shouldn't accrue late fees either) plus Payment Plan, which
 * has always been documented to exempt late fees.
 */
export const LATE_FEE_SUPPRESS_FLAG_CODES: readonly AccountFlag[] = ['PP', 'Pre-Ch13', 'X'];

/** True when any of the account's flags pauses automatic dues posting. */
export const flagsPauseBilling = (flags: readonly string[] | null | undefined): boolean =>
	!!flags && flags.some((f) => (BILLING_PAUSE_FLAG_CODES as readonly string[]).includes(f));

/** True when any of the account's flags suppresses automatic late fees. */
export const flagsSuppressLateFees = (flags: readonly string[] | null | undefined): boolean =>
	!!flags && flags.some((f) => (LATE_FEE_SUPPRESS_FLAG_CODES as readonly string[]).includes(f));

export const ACCOUNT_FLAG_LABELS: Record<AccountFlag, string> = {
	'DL': 'Demand Letter',
	'L': 'Lien',
	'F-Assoc': 'Foreclosure (Assoc)',
	'F-Bank': 'Foreclosure (Bank)',
	'SS': 'Sheriff Sale',
	'Pre-Ch13': 'Pre Chapter 13',
	'Post-Ch13': 'Post Chapter 13',
	'R': 'Renter',
	'PP': 'Payment Plan',
	'X': 'Pause Payments',
	'ACH': 'ACH Payment',
};

export const ACCOUNT_FLAG_DESCRIPTIONS: Record<AccountFlag, string> = {
	'DL': 'A demand letter has been issued for unpaid balances.',
	'L': 'A lien has been recorded against this property.',
	'F-Assoc': 'Foreclosure proceedings initiated by the association.',
	'F-Bank': 'Foreclosure proceedings initiated by the bank.',
	'SS': 'Sheriff sale scheduled or completed.',
	'Pre-Ch13': 'Pre Chapter 13 bankruptcy — pauses auto-billing and triggers an account split into a Post-Ch13 sibling on confirmation.',
	'Post-Ch13': 'Post Chapter 13 sibling account, created on Pre-Ch13 split.',
	'R': 'Unit is renter-occupied — separate billing contact may apply.',
	'PP': 'Active payment plan — automatic late fees are suppressed.',
	'X': 'Pause Payments — automatic dues posting and late fees are suppressed for this account. It still appears (unchecked) in the Post Dues preview so it can be force-posted as a manual override.',
	'ACH': 'Owner pays by ACH / autopay (imported from the QuickBooks "`" marker).',
};

/**
 * Dark "almost-black" badge colors grouped by severity:
 * - red: foreclosure family (F-Assoc, F-Bank, SS)
 * - amber: collections (DL, L)
 * - blue: occupancy (R)
 * - default slate for the rest (bankruptcy + payment plan)
 */
export const ACCOUNT_FLAG_COLORS: Record<AccountFlag, string> = {
	'DL': '#6b4f00',
	'L': '#6b4f00',
	'F-Assoc': '#7f1d1d',
	'F-Bank': '#7f1d1d',
	'SS': '#7f1d1d',
	'Pre-Ch13': '#37474f',
	'Post-Ch13': '#37474f',
	'R': '#0d47a1',
	'PP': '#37474f',
	'X': '#6b4f00',
	'ACH': '#1b5e20',
};

export interface Account extends BaseModel {
	associationId: string;
	/**
	 * Freeform unit/lot address (shortcut-code expandable like work_orders.jobLocation).
	 * No separate Address table — the string is authoritative.
	 */
	address?: string;
	accountNumber?: string;
	/** On-property lot identifier for the mailed statement (e.g. "12B"). May
	 *  differ from `accountNumber`, which is the accounting/payment code. */
	lotNumber?: string;
	status: AccountStatus;
	openedOn?: number;
	closedOn?: number;
	/**
	 * OPTIONAL because the API omits it for a reader without the `accounting` flag.
	 *
	 * `GET /accounts` and `GET /accounts/:id` answer for every authenticated user — the
	 * record is a lot and its owners — but the money is stripped by `toJson`'s
	 * `showFinancials` projection (api/accounts.ts). Declaring it required would let a
	 * consumer add up a field that is genuinely absent and render a confident `$0.00`.
	 * Same reason for `parValueBps`, `oldestDueOn` and the dues overrides below, which
	 * were already optional.
	 */
	balanceCents?: number;
	lastPostedThrough?: number;
	/** Earliest unpaid charge.due_on across this account, or undefined when
	 *  none are outstanding. Returned only by list endpoints; used for
	 *  delinquency-days display. */
	oldestDueOn?: number;
	/** Dues overrides — see utils/accounting-dues.ts for resolution order.
	 *  Nullable: a PUT sends `null` to clear an override (undefined would be
	 *  dropped by JSON.stringify and leave the prior value untouched). */
	duesOverrideAmountCents?: number | null;
	duesOverridePercentBps?: number | null;
	/** @deprecated Dues levels were removed (#232 par-value supersedes them).
	 *  Column retained for legacy rows; no longer drives dues resolution. */
	duesLevel?: number | null;
	/** Ownership share in basis points of 100% (307 = 3.07%). When the
	 *  association is in par-value mode with a budget, dues derive from this
	 *  share of the operating budget (#232). Nullable to clear. */
	parValueBps?: number | null;
	/** Free-form customer classification — floorplan/model like "Verona" or
	 *  "Townhome" (#225). */
	customerType?: string;
	/** Legal name of the entity that owns the unit, held on the ACCOUNT rather
	 *  than on an attached person — a builder still holding a spec home, the
	 *  clubhouse, a bank-owned foreclosure. Outranks the primary owner's
	 *  `companyName` in `accountDisplayName`. */
	companyName?: string;
	/** Soft-archive. Archived accounts stay for audit/history but are
	 * filtered out of default listings. Super-admin-only operation. */
	archivedAt?: string;
	/** Ownership-transfer audit links. When a unit sells, the old account is
	 *  closed+renamed and `transferredToAccountId` points at the new sibling;
	 *  the new account's `transferredFromAccountId` points back. `transferredOn`
	 *  is the business date (UTC-midnight ms) the transfer took effect. */
	transferredFromAccountId?: string;
	transferredToAccountId?: string;
	transferredOn?: number;
	createdBy?: string;
	importBatchId?: string;
	/** Original customer name from the source system (e.g. QuickBooks
	 * CUST.NAME). Populated by bulk importers so downstream transaction
	 * imports can match sections by exact string rather than having to
	 * tie-break via attached-person names. */
	externalName?: string;
	/** First attached owner ("FirstName LastName"), denormalized from
	 * account_people on list reads so labels can show "742PS · Bradley Jones"
	 * without an extra round-trip. Undefined if no owner is attached. */
	primaryOwnerName?: string;
	/** Company/legal name of the primary owner, denormalized from people on list
	 *  reads so the bulk-mailings envelope can print it above the name (#227). */
	primaryOwnerCompanyName?: string;
	/** Source-system primary key (e.g. QB CUST.REFNUM). Distinct from
	 * `accountNumber` (the human-readable code like "742PS") and from
	 * our internal `id` (UUID). Enables idempotent re-imports. */
	externalId?: string;
	/** Mailing address (where statements get sent). Distinct from `address`
	 *  which is the unit/property address. Off-site owners typically have
	 *  these set; on-site owners leave them null and statements use `address`. */
	mailingAddress?: string;
	/** Optional "c/o" line (part of the billing address). */
	mailingCareOf?: string;
	mailingCity?: string;
	mailingState?: string;
	mailingZip?: string;
	/** Advisory tags from `ACCOUNT_FLAG_CODES`. See issue #170. */
	flags?: AccountFlag[];
	/** Computed (not stored): true when any attached person carries a non-empty
	 *  email. Returned only by the accounts list endpoint via an EXISTS subquery;
	 *  powers the synthetic "Has Email" tag in the bulk-mailings picker (so you can
	 *  exclude owners who already get emailed). */
	hasEmail?: boolean;
}

/**
 * One line of an association's multi-GL dues template. The cron pro-rates
 * these line ratios to each account's resolved total when posting period
 * charges, producing one charge per line with its own gl_code_id (T-chart
 * accounting). When no lines are configured, the cron falls back to a
 * single un-split charge for the whole amount.
 */
export interface AssociationDuesLine {
	id: string;
	glCodeId?: string;
	/** Joined from `gl_codes` on read. `label` is optional free text the settings
	 *  UI never sets, so the GL code is what actually names a line on screen. */
	glCode?: string;
	glName?: string;
	fundId?: string;
	label?: string;
	amountCents: number;
	sortOrder: number;
	/** When set, this line is scoped to a specific dues level (the cron uses
	 *  it instead of the default template for accounts on that level). When
	 *  undefined, the line belongs to the association's default template. */
	levelId?: string;
}

export enum AccountPersonRole {
	Owner = 'owner',
	Resident = 'resident',
	BillingContact = 'billing_contact',
	EmergencyContact = 'emergency_contact',
}

export interface AccountPerson extends BaseModel {
	accountId: string;
	personId: string;
	role: AccountPersonRole;
	importBatchId?: string;
}

export enum ChargeCategory {
	Assessment = 'assessment',
	LateFee = 'late_fee',
	Special = 'special',
	Adjustment = 'adjustment',
	Other = 'other',
}

export enum ChargeSource {
	Manual = 'manual',
	Recurring = 'recurring',
	LateFeeAuto = 'late_fee_auto',
}

export interface Charge extends BaseModel {
	accountId: string;
	postedOn: number;
	dueOn?: number;
	amountCents: number;
	/** Sum of payment_applications applied to this charge. Denormalized. */
	paidCents?: number;
	/** Free-form label; defaults nudge users toward known buckets but
	 *  associations may invent their own (e.g. "Clubhouse"). */
	category: string;
	memo?: string;
	source: ChargeSource;
	sourceRef?: string;
	createdBy?: string;
	importBatchId?: string;
	/** Optional link to the invoice this charge appears on as a line item. */
	invoiceId?: string;
	/** Optional income GL bucket — usually set when the charge was created
	 *  from an Item preset. Falls through to reports without affecting
	 *  balance math; the P&L picks it up when bucketing income. */
	glCodeId?: string;
	/** Read-only GL code number + name, joined from gl_codes on reads that need
	 *  to display the per-line account (e.g. the invoice detail page). */
	glCodeNumber?: string;
	glCodeName?: string;
	/** Set when the charge has been waived (legacy — #682 replaced waive with write-off). The
	 *  charge stays in the ledger for audit; balance recompute excludes waived charges. */
	waivedAt?: number;
	waivedBy?: string;
	waiveReason?: string;
	/** The charge's active write-off credit and the amount it settles (#682). */
	writeOffCreditId?: string;
	writtenOffCents?: number;
}

export enum InvoiceStatus {
	Open = 'open',
	Partial = 'partial',
	Paid = 'paid',
	Void = 'void',
}

export interface Invoice extends BaseModel {
	/** What live write-offs (#682) settle across the invoice's lines. */
	writtenOffCents?: number;
	associationId: string;
	accountId: string;
	invoiceNumber?: string;
	issuedOn: number;
	dueOn?: number;
	totalCents: number;
	paidCents: number;
	status: InvoiceStatus;
	memo?: string;
	externalId?: string;
	externalRef?: string;
	importBatchId?: string;
	createdBy?: string;
	/** Issue #157: queued for batch print until cleared by the user. */
	printQueued?: boolean;
	/** Wall-clock ms when this invoice was last printed (null = never).
	 * Set by the bulk-print action; distinct from `printQueued` so emailed
	 * or exported invoices stay out of the queue. */
	printedAt?: number;
}

/** Invoice with its line-item charges pre-joined. */
export interface InvoiceWithCharges extends Invoice {
	charges: Charge[];
}

export type MemorizedInvoiceCadence = 'monthly' | 'quarterly' | 'annual';

export interface MemorizedInvoiceLine {
	id: string;
	memorizedInvoiceId: string;
	amountCents: number;
	category: string;
	memo?: string;
	sortOrder: number;
	createdAt: string;
	updatedAt: string;
}

export interface MemorizedInvoice extends BaseModel {
	associationId: string;
	accountId: string;
	name: string;
	cadence: MemorizedInvoiceCadence;
	dayOfMonth: number;
	startOn?: number;
	endOn?: number;
	active: boolean;
	invoiceNumberPrefix?: string;
	memo?: string;
	lastPostedPeriod?: string;
	lastPostedAt?: number;
	createdBy?: string;
}

export interface MemorizedInvoiceWithLines extends MemorizedInvoice {
	lines: MemorizedInvoiceLine[];
}

export type MemorizedBillCadence = 'monthly' | 'quarterly' | 'annual';

export interface MemorizedBillLine {
	id: string;
	memorizedBillId: string;
	amountCents: number;
	glCodeId?: string;
	description?: string;
	memo?: string;
	sortOrder: number;
	createdAt: string;
	updatedAt: string;
}

export interface MemorizedBill extends BaseModel {
	associationId: string;
	vendorId: string;
	name: string;
	cadence: MemorizedBillCadence;
	dayOfMonth: number;
	startOn?: number;
	endOn?: number;
	active: boolean;
	billNumberPrefix?: string;
	memo?: string;
	lastPostedPeriod?: string;
	lastPostedAt?: number;
	createdBy?: string;
}

export interface MemorizedBillWithLines extends MemorizedBill {
	lines: MemorizedBillLine[];
}

/** A single payment-to-charge allocation (the row in payment_applications). */
export interface PaymentApplication {
	id: string;
	paymentId: string;
	chargeId: string;
	amountCentsApplied: number;
	createdAt: string;
}

export enum PaymentMethod {
	Cash = 'cash',
	Check = 'check',
	ACH = 'ach',
	Card = 'card',
	Other = 'other',
}

export interface Payment extends BaseModel {
	accountId: string;
	receivedOn: number;
	amountCents: number;
	method: PaymentMethod;
	reference?: string;
	memo?: string;
	createdBy?: string;
	importBatchId?: string;
	/** Set once the payment has been included on a deposit slip. NULL = sitting in undeposited funds. */
	depositId?: string;
}

/**
 * One row representing both sides of an inter-bank transfer. Appears on
 * both registers (outflow on `fromBankId`, inflow on `toBankId`) and each
 * side clears independently because they land on separate statements.
 */
export interface BankTransfer extends BaseModel {
	associationId: string;
	fromBankId: string;
	toBankId: string;
	transferredOn: number;
	amountCents: number;
	memo?: string;
	reference?: string;
	fromClearedAt?: number;
	toClearedAt?: number;
	fromReconciliationId?: string;
	toReconciliationId?: string;
	importBatchId?: string;
	createdBy?: string;
}

/**
 * Deposit slip — groups N payments going to one bank account on one trip.
 * Created via the Record Deposit workflow once the user has finished entering
 * individual payments. Drives the printable summary slip and the bank's
 * single ledger line per deposit (vs. one per payment) at reconciliation.
 */
export interface Deposit extends BaseModel {
	associationId: string;
	bankAccountId: string;
	depositedOn: number;
	memo?: string;
	totalCents: number;
	/** Per-association sequential slip number, starting at 10000. Optional
	 * because deposits created before migration 106 may lack one until the
	 * backfill runs (which it does as part of the same migration). */
	slipNumber?: number;
	printedAt?: number;
	createdBy?: string;
}

/** Row shape returned by /deposits/undeposited and /deposits/:id/payments. */
export interface DepositablePayment {
	id: string;
	accountId: string;
	receivedOn: number;
	amountCents: number;
	method: PaymentMethod;
	reference?: string;
	memo?: string;
	bankAccountId?: string;
	accountNumber?: string;
	address?: string;
	associationId: string;
	/** Account display-name inputs (#322) — the deposit slip renders
	 *  accountDisplayName() from these. Populated by /deposits/:id/payments. */
	externalName?: string;
	primaryOwnerName?: string;
	/** Comma-joined flag codes for the account. */
	flags?: string;
}

export interface Credit extends BaseModel {
	accountId: string;
	issuedOn: number;
	amountCents: number;
	memo?: string;
	/** Optional human-readable credit memo number, like "CM-1024". */
	creditNumber?: string;
	fundId?: string;
	/** Income GL the contra-revenue leg debits. Unset → Unassigned Income. */
	glCodeId?: string;
	createdBy?: string;
	importBatchId?: string;
}

export interface Refund extends BaseModel {
	accountId: string;
	paymentId?: string;
	issuedOn: number;
	amountCents: number;
	method: PaymentMethod;
	memo?: string;
	/** Bank GL the money leaves from. Unset → Unassigned Expense. */
	glCodeId?: string;
	createdBy?: string;
}

export enum AssessmentCadence {
	Monthly = 'monthly',
	Quarterly = 'quarterly',
	Annual = 'annual',
}

export interface AssessmentSchedule extends BaseModel {
	associationId: string;
	name: string;
	amountCents: number;
	cadence: AssessmentCadence;
	startOn: number;
	endOn?: number;
	dayOfMonth: number;
	category: ChargeCategory.Assessment | ChargeCategory.Special | ChargeCategory.Other;
	active: boolean;
	createdBy?: string;
}

export interface LateFeeRule extends BaseModel {
	associationId: string;
	graceDays: number;
	amountCents?: number;
	percentBps?: number;
	maxPerCycle: number;
	capCents?: number;
	active: boolean;
	createdBy?: string;
}

export interface AccountNote extends BaseModel {
	accountId: string;
	body: string;
	createdBy?: string;
	createdByName?: string;
}

export type FundKind = 'operating' | 'reserve' | 'capital' | 'special';
export interface Fund extends BaseModel {
	associationId: string;
	name: string;
	kind: FundKind;
	isDefault: boolean;
}

export interface FiscalPeriod extends BaseModel {
	associationId: string;
	periodStart: number;
	periodEnd: number;
	closedAt: number;
	closedBy?: string;
	reopenedAt?: number;
	reopenedBy?: string;
	reopenReason?: string;
}

/**
 * A finalized month: the immutable close record pointing at the R2 archive
 * bundle (every report frozen as of month-end) with integrity hashes. Editing
 * the month is reopen-to-edit — reopening supersedes the archive. Not a
 * BaseModel: the table tracks `closedAt`/`reopenedAt`, not created/updated.
 */
export interface MonthClose {
	id: string;
	associationId: string;
	year: number;
	month: number;
	closedAt: number;
	closedBy?: string;
	entriesArchived: number;
	linesArchived: number;
	trialBalanceDebitsCents: number;
	trialBalanceCreditsCents: number;
	archiveSha256: string;
	archiveByteSize: number;
	reopenedAt?: number;
	reopenedBy?: string;
	reopenedReason?: string;
}

/**
 * A unified ledger entry for an account, used by the account detail view and
 * payment-history report. Each row is derived from one of charges, payments,
 * credits, or refunds.
 */
export type LedgerEntryKind = 'charge' | 'payment' | 'credit' | 'refund';

export interface LedgerEntry {
	kind: LedgerEntryKind;
	id: string;
	accountId: string;
	/** Date of effect: posted_on, received_on, or issued_on depending on kind. */
	occurredOn: number;
	/** Signed amount: charges & refunds positive, payments & credits negative. */
	signedAmountCents: number;
	/** Absolute value of the underlying row's amount_cents. */
	amountCents?: number;
	memo?: string;
	category?: string;
	method?: PaymentMethod;
	reference?: string;
	createdBy?: string;
	createdAt: string;
	/** Charge rows only: portion settled by payment applications. */
	paidCents?: number;
	/** Charge rows only: derived status from paidCents vs amountCents.
	 *  'waived' means the charge has been written off — it remains visible
	 *  in the ledger but is excluded from the account balance. */
	chargeStatus?: 'paid' | 'partial' | 'unpaid' | 'waived';
	/** Charge rows only: invoice this charge is grouped under, if any. */
	invoiceId?: string;
	/** Charge rows only: human-readable invoice number, joined from invoices. */
	invoiceNumber?: string;
	/** Charge rows only: per-line GL split (when dues are fanned out across
	 *  multiple GL codes, each `charges` row carries its own gl_code_id). */
	glCodeId?: string;
	glCodeNumber?: string;
	glCodeName?: string;
	/** True when this row's effective date (`posted_on`/`received_on`/
	 *  `issued_on`) is after the as-of date. Future rows are surfaced for
	 *  visibility but excluded from the running balance. */
	isFuture?: boolean;
	/** Charge rows only: timestamp the charge was waived (legacy — no UI creates waives any
	 *  more; a write-off replaced them, #682). */
	waivedAt?: number;
	waiveReason?: string;
	waivedBy?: string;
	/** Charge rows only: the charge's active write-off credit and the amount it settles (#682). */
	writeOffCreditId?: string;
	writtenOffCents?: number;
	/** Credit rows only: set when the credit is a write-off of this charge (#682). */
	writeOffChargeId?: string;
	/** Payment rows only: timestamp the payment was voided. Voided payments
	 *  remain visible in the ledger but are excluded from balances. */
	voidedAt?: number;
	voidReason?: string;
	/** Payment rows only: timestamp the payment was returned (NSF/bounced).
	 *  Like voided, the row stays visible but is excluded from balances; the
	 *  bank claw-back is a separate forward-dated returned_payment entry. */
	returnedAt?: number;
}

export interface ReturnedPayment extends BaseModel {
	associationId: string;
	paymentId: string;
	accountId: string;
	bankAccountId?: string;
	returnedOn: number;
	amountCents: number;
	reason?: string;
	nsfFeeChargeId?: string;
	sourceEntryId?: string;
	clearedAt?: number;
	reconciliationId?: string;
	memo?: string;
	createdBy?: string;
}



/**
 * General Ledger (Chart of Accounts) code categories. These mirror the
 * QuickBooks ACCNTTYPE column so IIF imports are a direct map. The app
 * primarily cares about INC (income), EXP (expense), and EXEXP (capital
 * expense); the bank/AR/AP/equity types are imported for completeness
 * so round-trip exports stay faithful.
 */
export enum GlCodeAccountType {
	Bank = 'BANK',
	AccountsReceivable = 'AR',
	AccountsPayable = 'AP',
	Income = 'INC',
	Expense = 'EXP',
	CapitalExpense = 'EXEXP',
	OtherCurrentAsset = 'OCASSET',
	OtherCurrentLiability = 'OCLIAB',
	Equity = 'EQUITY',
	FixedAsset = 'FIXASSET',
	LongTermLiability = 'LTLIAB',
	CostOfGoodsSold = 'COGS',
	OtherIncome = 'OINC',
	OtherExpense = 'OEXP',
	NonPosting = 'NONPOSTING',
	Other = 'OTHER',
}

export interface GlCode extends BaseModel {
	code: string;
	name: string;
	/** IIF-style "PARENT:CHILD" full path (for tree rendering without recursive SQL). */
	fullPath: string;
	parentId?: string;
	accountType: GlCodeAccountType;
	description?: string;
	active: boolean;
	sortOrder: number;
	/** Whether overdue charges posted to this GL code can draw a late fee. Only
	 *  settable on INC (Income) codes — enforced by the API. A charge with NO GL
	 *  code does NOT trigger: eligibility is a hard gate, so an un-coded charge
	 *  can't be allowed to decide whether a fee posts at all. (#245) */
	triggersLateFee?: boolean;
	/** Days a charge on this GL code shelters the account's balance from a late fee,
	 *  up to the charge amount. Undefined / 0 = no grace. 30 on Water & Sewer (3025)
	 *  reproduces the #304 water rule, which used to be inferred from
	 *  `triggersLateFee === false`. */
	paymentGraceDays?: number;
	/** Owning association. Undefined / null = company-wide default template. */
	associationId?: string;
	createdBy?: string;
	importBatchId?: string;
	/** Source-system primary key (e.g. QB ACCNT.REFNUM). */
	externalId?: string;
}

/**
 * QuickBooks-style line-item preset. Picking an Item on an invoice line
 * pre-fills the description, GL code, and (optionally) the unit price —
 * each is still editable on the line before save.
 *
 * Scope follows the gl_codes pattern: `associationId` undefined → the
 * company-wide default template (seeded in migration 134); set →
 * association-specific override that shadows the default of the same
 * `itemCode`.
 */
export interface Item extends BaseModel {
	associationId?: string;
	itemCode: string;
	name: string;
	description?: string;
	glCodeId?: string;
	defaultPriceCents: number;
	active: boolean;
	sortOrder: number;
	externalId?: string;
	createdBy?: string;
	importBatchId?: string;
}

export interface Vendor extends BaseModel {
	name: string;
	printAs?: string;
	companyName?: string;
	contactFirstName?: string;
	contactMiddleInit?: string;
	contactLastName?: string;
	email?: string;
	phone?: string;
	phoneAlt?: string;
	fax?: string;
	addressLine1?: string;
	addressLine2?: string;
	addressLine3?: string;
	city?: string;
	state?: string;
	zip?: string;
	/** Federal tax ID (EIN/SSN). Stored as an `sf1:iv:ct` ciphertext blob —
	 *  encrypted client-side with the org-wide Secure Fields password. The
	 *  backend treats this as opaque text; only the client ever decrypts. */
	taxId?: string;
	/** Client-computed display mask for `taxId` (e.g. `***-**-1234`). Saved
	 *  at the same time as the ciphertext so list views can render without
	 *  forcing every user to unlock. */
	taxIdMask?: string;
	/** If true, vendor payments roll up into the year-end 1099-NEC report. */
	is1099: boolean;
	vendorType?: string;
	terms?: string;
	accountNumber?: string;
	/** Default expense-side GL bucket for bills from this vendor. */
	defaultGlCodeId?: string;
	notes?: string;
	active: boolean;
	createdBy?: string;
	importBatchId?: string;
	/** Source-system primary key (e.g. QB VEND.REFNUM). */
	externalId?: string;
}

/**
 * Import batch — an audit record tagging all rows created during a single
 * bulk import operation. Every row that was created as part of the batch
 * carries the same `import_batch_id`, which makes revert a one-SQL-per-
 * table operation: set `deleted_at = now()` where `import_batch_id = ?`.
 *
 * `summary` is an opaque JSON string the importer populates with per-table
 * counts + context (source filename, association, etc).
 */
export enum ImportBatchKind {
	IifLists = 'iif_lists',
	CsvTransactions = 'csv_transactions',
	CsvVendorTransactions = 'csv_vendor_transactions',
	CsvJournal = 'csv_journal',
	CsvFreshStart = 'csv_fresh_start',
	StartingBalances = 'starting_balances',
	/** Ownership percentages set on existing accounts (par-value associations). */
	ParValues = 'par_values',
	/** Utility bill register posted as per-account charges. */
	WaterBills = 'water_bills',
	DuesPosting = 'dues_posting',
	LateFeePosting = 'late_fee_posting',
	QbBundle = 'qb_bundle',
	ReapplyPayments = 'reapply_payments',
	AssociationReset = 'association_reset',
	/**
	 * One run of the ownership-transfer packet intake — a POST of records or a
	 * Dropbox poll. Scopes the STAGING rows only: the accounts and people an
	 * approved transfer creates are deliberately left untagged, because a revert
	 * could soft-delete them while being unable to un-close the seller's account.
	 */
	OwnershipTransfers = 'ownership_transfers',
	/**
	 * A data repair that UPDATES existing accounts (#635). Its writes are logged in
	 * `import_batch_field_changes`, and its revert writes the old values back — it
	 * never soft-deletes, because the accounts were not created by it.
	 */
	AccountRepair = 'account_repair',
	Manual = 'manual',
}

export interface ImportBatch extends BaseModel {
	kind: ImportBatchKind;
	associationId?: string;
	/** Association display name (LEFT JOIN) — for the batch-list UI. */
	associationName?: string;
	sourceFilename?: string;
	/** JSON-encoded per-table counts + anything the importer wants to record. */
	summary?: string;
	createdBy?: string;
	/** Display name of the user who created the batch (LEFT JOIN users). */
	createdByName?: string;
	revertedAt?: string;
	revertedBy?: string;
	/** Display name of the user who reverted the batch (LEFT JOIN users). */
	revertedByName?: string;
	/** Cloudflare Workflow instance id when a durable import is driving this batch. */
	workflowId?: string;
}

/** One raw uploaded file recorded against a workflow-backed import batch. */
export interface ImportBatchFile {
	/** R2 object key — pass to the download endpoint, never used directly. */
	r2Key: string;
	filename: string;
	detectedType?: string | null;
	overrideType?: string | null;
	byteSize?: number | null;
	/** pending | processing | done | error */
	status: string;
	/** Per-file parse counts / errors (JSON parsed). */
	summary?: unknown;
}

export interface ImportBatchDetails extends ImportBatch {
	/** Per-table live (non-deleted, non-reverted) row counts. */
	counts: {
		accounts: number;
		people: number;
		accountPeople: number;
		charges: number;
		payments: number;
		credits: number;
		glCodes: number;
		vendors: number;
		bills: number;
		billPayments: number;
	};
	/** Field values the batch overwrote on rows it did not create (`account_repair`, #635). */
	fieldChanges?: { total: number; reverted: number; restored: number; skipped: number };
	/** Raw uploaded files (workflow-backed batches only); empty otherwise. */
	files?: ImportBatchFile[];
	/** Live workflow step beacon (issue #248) — the EXACT step, e.g. 'spine chunk 12'. */
	currentStep?: string | null;
	/** That step's recorded input/output, e.g. { output: { remaining: 3000 } }. */
	currentStepDetail?: { input?: unknown; output?: unknown } | null;
	/** ms epoch the beacon was last written. */
	currentStepAt?: number | null;
	/** Best-effort end-of-work timestamp (last beacon / file write / revert). */
	finishedAt?: string | null;
	/** How long the import took, ms (created → finishedAt). Null while running. */
	durationMs?: number | null;
}

/** One GL line of a journal-only entry. */
export interface JournalOnlyLine {
	glCode: string | null;
	glName: string | null;
	debitCents: number;
	creditCents: number;
	memo: string | null;
}

/**
 * A journal-only entry (issue #248) — posted to the ledger during a QB import
 * but with no customer/vendor domain row (General Journal, payee-less check, or
 * a projection that failed). Sourced from `projection_audit`.
 */
export interface JournalOnlyEntry {
	entryId: string;
	/** 'unclassified' | 'error' */
	classification: string;
	reason: string | null;
	postedOn: number;
	/** QB transaction Num (assocId prefix already stripped). */
	num: string;
	memo: string | null;
	amountCents: number;
	lines: JournalOnlyLine[];
}

/**
 * Per-association bank accounts (checking, money market, savings).
 * Each association can have multiple; exactly one can be the default
 * which receives new bills/deposits unless the user picks another.
 *
 * `currentBalanceCents` is the denormalized running balance (sum of
 * cleared deposits − cleared bill_payments + opening_balance + interest
 * − fees). It's recomputed after every deposit-complete and
 * bill-payment write; not authoritative on its own — the source of
 * truth is the underlying transactions.
 */
export enum BankAccountKind {
	Checking = 'checking',
	MoneyMarket = 'money_market',
	Savings = 'savings',
	Cd = 'cd',
	Other = 'other',
}

export type CheckLayout = 'top_stub' | 'middle_stub' | 'bottom_stub' | 'three_per_page';

export interface BankAccount extends BaseModel {
	associationId: string;
	name: string;
	kind: BankAccountKind;
	institution?: string;
	accountNumberLast4?: string;
	routingNumberLast4?: string;
	/** Full account number — stored for ACH/check printing. Reveal-on-edit;
	 * list views show only `accountNumberLast4`. */
	accountNumberFull?: string;
	/** Full routing number — same handling as `accountNumberFull`. */
	routingNumberFull?: string;
	currentBalanceCents: number;
	isDefault: boolean;
	active: boolean;
	notes?: string;
	/** Optional link to the cash GL code on the chart of accounts. */
	glCodeId?: string;
	/** Remembered offsetting GL for interest earned, used to prefill the
	 *  Start-reconciliation adjustments (#428). Set by using it, not by a config
	 *  screen — the modal persists whatever the user actually posted against. */
	interestGlCodeId?: string;
	/** Remembered offsetting GL for bank service charges (#428). */
	bankChargeGlCodeId?: string;
	/** Denormalized cash GL code (e.g. `1010`) — populated by GET enrichment so list views can render it. */
	glCode?: string;
	/** Denormalized cash GL name (e.g. `Checking`) — populated by GET enrichment. */
	glCodeName?: string;
	/** Parent GL code id when this bank's GL is a sub-account (`gl_codes.parent_id`) — GET enrichment (#353). */
	glParentId?: string;
	/** Parent GL code (e.g. `1030`) when this bank's GL is a sub-account — GET enrichment. */
	glParentCode?: string;
	/** Parent GL name when this bank's GL is a sub-account — GET enrichment. */
	glParentName?: string;
	createdBy?: string;
	nextCheckNumber?: number;
	checkLayout?: CheckLayout;
	bankAddressLine1?: string;
	bankAddressLine2?: string;
	bankCity?: string;
	bankState?: string;
	bankZip?: string;
	signatureImageR2Key?: string;
}

/**
 * Bank register row kind emitted by `GET /bank-accounts/:id/register`.
 *
 * - `deposit`, `bill_payment`, `transfer_in`, `transfer_out`: hand-entered or
 *   projected from source tables.
 * - `journal`: orphan journal entries (lines hitting this bank's GL with no
 *   back-reference from any source table). Surfaced so the register's running
 *   balance ties out to canonical `journal_lines` — see
 *   `derivedBankBalanceSqlExpr` in `api/accounting/ledger.ts`.
 */
export type BankRegisterRowKind =
	| 'deposit'
	| 'bill_payment'
	| 'transfer_in'
	| 'transfer_out'
	| 'returned_payment'
	| 'journal';

export interface BankRegisterChildPayment {
	id: string;
	receivedOn: number;
	amountCents: number;
	method?: string;
	reference?: string;
	memo?: string;
	clearedAt?: number;
	reconciliationId?: string;
	counterparty?: string;
	counterpartyId?: string;
}

/** A single bill paid by a collapsed multi-bill check register row. Lets the UI
 *  offer an expandable "N bills" affordance instead of mis-navigating to one. */
export interface BankRegisterChildBill {
	billId: string;
	billNumber?: string | null;
}

export interface BankRegisterRow {
	kind: BankRegisterRowKind;
	id: string;
	occurredOn: number;
	signedAmountCents: number;
	amountCents: number;
	method?: string;
	reference?: string;
	checkNumber?: string;
	memo?: string;
	counterparty?: string;
	counterpartyId?: string;
	/** Scalar drill-target bill. Set ONLY for a single-bill payment (NULL check
	 *  or a check paying exactly one bill). Left undefined for a genuine
	 *  multi-bill check so the UI does not navigate to an arbitrary bill —
	 *  it renders `bills[]` instead. */
	billId?: string;
	/** bill_payment rows collapsed from a multi-bill check: the owning check.id.
	 *  The register row id equals this checkId for collapsed rows. */
	checkId?: string;
	/** All bills paid by a collapsed check row (only populated when >1 bill). */
	bills?: BankRegisterChildBill[];
	clearedAt?: number;
	reconciliationId?: string;
	runningBalanceCents: number;
	paymentCount?: number;
	payments?: BankRegisterChildPayment[];
	sourceEntryId?: string;
	relatedEntryIds?: string[];
	/** returned_payment rows: the matched original payment that bounced, for
	 *  drilling to it on the resident ledger. NULL when no confident match. */
	originalPaymentId?: string;
	categoryGlCodeId?: string;
	categoryGlCode?: string;
	categoryGlCodeName?: string;
	categoryAccountType?: string;
}

/**
 * Bill (AP) — what the association owes a vendor. Multi-line: each
 * bill_line_item attributes to a GL code so P&L can roll bills up by
 * expense bucket.
 *
 * `paidCents` is the denormalized sum of bill_payments for this bill;
 * `status` derives from total vs paid.
 */
export enum BillStatus {
	Open = 'open',
	Partial = 'partial',
	Paid = 'paid',
	Void = 'void',
}

export interface Bill extends BaseModel {
	associationId: string;
	vendorId: string;
	billNumber?: string;
	issuedOn: number;
	dueOn?: number;
	totalCents: number;
	paidCents: number;
	status: BillStatus;
	memo?: string;
	externalId?: string;
	externalRef?: string;
	importBatchId?: string;
	createdBy?: string;
}

/**
 * Where a work order sits in the billing pipeline.
 *
 * `unbilled` is a DERIVED state, not a stored one — an approved work order with
 * no `work_order_billings` row at all. The approval hook writes the `pending`
 * marker best-effort, so the queue must treat a missing row as billable rather
 * than as "already handled".
 */
export type WorkOrderBillingStatus = 'unbilled' | 'pending' | 'posted' | 'skipped';

/** One cost category of a generated bill line. */
export type WorkOrderCostCategory = 'labor' | 'materials' | 'misc' | 'adjustment';

/** A work order awaiting billing, as listed by GET /work-order-billing/queue. */
export interface WorkOrderBillingQueueItem {
	workOrderId: string;
	orderNumber: number | null;
	workRequested: string | null;
	jobLocation: string | null;
	completedAt: number | null;
	pmApproved: number | null;
	associationId: string;
	associationName: string | null;
	/** Approximate — the authoritative per-line cents come from the preview. */
	estimatedTotalCents: number;
	finalChargeSet: boolean;
	billingStatus: WorkOrderBillingStatus;
	skippedReason?: string;

	// ── Review workflow (#496). Populated on the needs_review / pending_approval tabs. ──
	/** Maintenance's internal completion note. Reviewers and admins only. */
	workCompleted?: string | null;
	/** Accounting's customer-facing rewrite — what the PM and the invoice will show. */
	customerDescription?: string | null;
	/** When accounting marked it ready. The SLA clock starts here. */
	reviewApprovedAt?: number | null;
	reviewedBy?: string | null;
	/** Set when a PM sent it back; such rows sort first in the review queue. */
	reviewReturnedAt?: number | null;
	reviewReturnNote?: string | null;
	/** The property manager who owes the approval, for the pending tab. */
	managerId?: string | null;
	managerName?: string | null;
}

/** Which slice of the work-order pipeline a queue request wants. */
export type WorkOrderQueueTab = 'needs_review' | 'pending_approval' | 'pending' | 'skipped';

/** One derived line of a not-yet-posted bill. */
export interface WorkOrderDraftLine {
	category: WorkOrderCostCategory;
	description: string;
	glCodeId: string;
	amountCents: number;
	memo?: string;
	sortOrder: number;
}

/** GET /work-order-billing/:id/preview — what the bill WOULD look like. */
export interface WorkOrderBillingPreview {
	workOrderId: string;
	associationId: string;
	orderNumber: number | null;
	workRequested: string | null;
	jobLocation: string | null;
	completedAt: number | null;
	lines: WorkOrderDraftLine[];
	computedTotalCents: number;
	finalChargeCents: number | null;
	adjustmentCents: number;
	/** Always exactly the sum of `lines`. */
	totalCents: number;
	vendorId?: string;
	vendorName?: string;
	nextInvoiceNumber: number;
	invoicePrefix: string;
}

/** GET /work-order-billing/:id — a work order's current billing state. */
export interface WorkOrderBillingState {
	id?: string;
	workOrderId: string;
	associationId?: string;
	status: WorkOrderBillingStatus;
	invoiceNumber?: number;
	billId?: string;
	billNumber?: string;
	totalCents?: number;
	billStatus?: BillStatus;
	skippedReason?: string;
	postedAt?: number;
	/** `posted` with no bill: invoiced outside the app and marked so by hand. */
	billedExternally?: boolean;
	/** Where the real invoice lives ("QB inv 4471"). Only with `billedExternally`. */
	externalReference?: string;
}

/** One posted result from a single post or a commit chunk. */
export interface WorkOrderBillingPostResult {
	workOrderId: string;
	billId: string;
	invoiceNumber: number;
	billNumber: string;
	totalCents: number;
	alreadyPosted: boolean;
}

/**
 * How a bill line's `glCodeId` resolves against the chart. A GL code can be
 * retired out from under a line at any time — deleting one only sets
 * `deleted_at`, and re-seeding the chart retires every code absent from the
 * default CSV — so "no GL code shown" has four distinct causes that need
 * different repairs. Resolved server-side by GET /bills/:id.
 */
export type GlLineStatus =
	/** Resolves to a live, active GL code. */
	| 'ok'
	/** `glCodeId` is null — nothing was ever assigned. */
	| 'unassigned'
	/** Points at a GL code row that no longer exists (dangling reference). */
	| 'missing'
	/** The GL code was soft-deleted. */
	| 'deleted'
	/** The GL code exists but is deactivated. */
	| 'inactive';

export interface BillLineItem extends BaseModel {
	billId: string;
	sortOrder: number;
	description?: string;
	glCodeId?: string;
	amountCents: number;
	memo?: string;
	/** Present on GET /bills/:id; absent on list responses. */
	glCode?: string;
	glName?: string;
	glStatus?: GlLineStatus;
}

/** A bill record returned with its line items pre-joined. */
export interface BillWithLines extends Bill {
	lines: BillLineItem[];
	/**
	 * Present only on bills generated from a work order (GET /bills/:id).
	 * Absent on hand-entered and imported bills.
	 */
	sourceWorkOrder?: {
		id: string;
		orderNumber?: number;
		workRequested?: string;
		invoiceNumber?: number;
	};
}

export interface BillPayment extends BaseModel {
	billId: string;
	bankAccountId: string;
	paidOn: number;
	amountCents: number;
	method: PaymentMethod;
	checkNumber?: string;
	reference?: string;
	memo?: string;
	clearedAt?: number;
	reconciliationId?: string;
	importBatchId?: string;
	createdBy?: string;
}

export enum ReconciliationStatus {
	InProgress = 'in_progress',
	Finished = 'finished',
	Undone = 'undone',
}

export interface Reconciliation extends BaseModel {
	bankAccountId: string;
	periodStart: number;
	periodEnd: number;
	beginningBalanceCents: number;
	endingBalanceCents: number;
	clearedTotalCents: number;
	status: ReconciliationStatus;
	notes?: string;
	finishedAt?: number;
	finishedBy?: string;
	createdBy?: string;
}

export interface JournalLine {
	id: string;
	entryId: string;
	sortOrder: number;
	glCodeId: string;
	/** Denormalized GL code (e.g. `1100`) — populated by the journal-entry GET
	 *  enrichment so detail views can render the code without a second lookup. */
	glCode?: string;
	/** Denormalized GL name (e.g. `Accounts Receivable`) — same enrichment. */
	glCodeName?: string;
	fundId?: string;
	debitCents: number;
	creditCents: number;
	memo?: string;
	/** Sub-ledger pointer to an AR customer (accounts.id). At most one of
	 * accountId/bankAccountId/vendorId is set per line. */
	accountId?: string;
	bankAccountId?: string;
	vendorId?: string;
	createdAt: string;
}

export interface JournalEntry extends BaseModel {
	associationId: string;
	postedOn: number;
	memo?: string;
	source?: string;
	sourceRef?: string;
	reversesEntryId?: string;
	createdBy?: string;
	lines: JournalLine[];
}

export interface JournalLineInput {
	sortOrder?: number;
	glCodeId: string;
	fundId?: string;
	debitCents: number;
	creditCents: number;
	memo?: string;
	accountId?: string;
	bankAccountId?: string;
	vendorId?: string;
}

export interface BudgetLine {
	id: string;
	budgetId: string;
	glCodeId: string;
	janCents: number;
	febCents: number;
	marCents: number;
	aprCents: number;
	mayCents: number;
	junCents: number;
	julCents: number;
	augCents: number;
	sepCents: number;
	octCents: number;
	novCents: number;
	decCents: number;
	notes?: string;
	sortOrder: number;
	createdAt: string;
	updatedAt: string;
}

export interface Budget extends BaseModel {
	associationId: string;
	fundId?: string;
	fiscalYear: number;
	name?: string;
	notes?: string;
	createdBy?: string;
	lines: BudgetLine[];
}

export type BankStatementImportSource = 'csv' | 'ofx' | 'qfx';
export type BankStatementImportRowStatus = 'pending' | 'matched' | 'ignored' | 'manual';

export interface BankStatementImportRow {
	id: string;
	importId: string;
	occurredOn: number;
	signedAmountCents: number;
	description?: string;
	reference?: string;
	status: BankStatementImportRowStatus;
	matchedKind?: 'payment' | 'bill_payment';
	matchedId?: string;
	sortOrder: number;
	createdAt: string;
	updatedAt: string;
}

export interface BankStatementImport extends BaseModel {
	bankAccountId: string;
	filename?: string;
	sourceFormat: BankStatementImportSource;
	rowCount: number;
	matchedCount: number;
	createdBy?: string;
	rows?: BankStatementImportRow[];
}

export type CollectionsActionType =
	| 'letter_first_notice'
	| 'letter_second_notice'
	| 'letter_intent_to_lien'
	| 'lien_filed'
	| 'attorney_handoff'
	| 'payment_plan'
	| 'payment_plan_broken'
	| 'other';

export interface CollectionsAction extends BaseModel {
	accountId: string;
	actionType: CollectionsActionType;
	occurredOn: number;
	notes?: string;
	balanceAtActionCents?: number;
	createdBy?: string;
}

// ---------------------------------------------------------------------------
// Exterior Improvement Applications (architectural review) — #363
// ---------------------------------------------------------------------------

/** A reusable architectural-review ruleset. EPCON associations share one. */
export interface ApplicationRuleset extends BaseModel {
	name: string;
	isDefault?: boolean;
	requestTypes?: ApplicationRequestType[];
}

/** A descriptor for an extra form field on a request type. */
export interface ApplicationFieldDef {
	key: string;
	label: string;
	type: 'text' | 'number' | 'date' | 'select';
	required?: boolean;
	options?: string[];
}

/** A descriptor for a required upload on a request type. */
export interface ApplicationAttachmentDef {
	key: string;
	label: string;
	required?: boolean;
}

/** One kind of exterior request (fence, paint, roof, …) within a ruleset. */
export interface ApplicationRequestType extends BaseModel {
	rulesetId: string;
	name: string;
	slug: string;
	requiredFields: ApplicationFieldDef[];
	requiredAttachments: ApplicationAttachmentDef[];
	reviewDeadlineDays: number;
	sortOrder: number;
}

/** Per-association override of a request type's rules (NULL fields inherit). */
export interface AssociationRequestTypeOverride extends BaseModel {
	associationId: string;
	requestTypeId: string;
	reviewDeadlineDays?: number | null;
	/** NULL = inherit; false = hide this request type for this association. */
	enabled?: boolean | null;
}

/** Direction of an email logged against an application. */
export enum ApplicationCorrespondenceDirection {
	Outbound = 0,
	Inbound = 1,
}

/** One email (sent or received) threaded onto an application. */
export interface ApplicationCorrespondence extends BaseModel {
	applicationId: string;
	direction: ApplicationCorrespondenceDirection;
	fromAddress?: string;
	toAddress?: string;
	ccAddress?: string;
	subject?: string;
	bodyText?: string;
	bodyHtml?: string;
	messageId?: string;
	inReplyTo?: string;
	/** ISO timestamp a human first opened this message; null = still unread. */
	readAt?: string | null;
	createdBy?: string;
}

/**
 * An Exterior Improvement Application record. `status` is an integer mapping
 * `ExteriorApplicationStatus` (see `utils/application-status.ts`). `accountId` is
 * unset until a public submission is matched to a lot; `createdBy` is unset for
 * public (homeowner) submissions.
 */
export interface ExteriorApplication extends BaseModel {
	applicationNumber?: number;
	associationId: string;
	accountId?: string | null;
	requestTypeId: string;
	status: number;
	submitterName?: string;
	submitterEmail?: string;
	submitterPhone?: string;
	propertyAddress?: string;
	description?: string;
	/** Request-type-specific answers keyed by ApplicationFieldDef.key. */
	fields?: Record<string, unknown>;
	/** Staff-checked state for the request type's required-items checklist, keyed by item key. */
	checklistState?: Record<string, boolean>;
	reviewDeadline?: number | null;
	decisionNotes?: string;
	boardMeetingDate?: number | null;
	decidedBy?: string | null;
	decidedAt?: number | null;
	createdBy?: string | null;
}

/**
 * Which side owes the next move on a request. The rule that computes it lives in
 * `utils/application-waiting.ts`; the type lives here because this module is
 * import-free by design and everything else depends on it.
 */
export type WaitingSide = 'us' | 'owner' | 'none';

/**
 * Why two accounts were reported as duplicates of each other. Detection lives in
 * `api/accounts.ts` (`findDuplicateAccounts`).
 *
 * `shortcode` — the same shortcode on more than one account. The unique index is
 *   scoped per association, so these are always cross-association, and they are what
 *   makes shortcode-based intake matching ambiguous.
 * `mangled_number` — a shortcode carrying stray quotes/whitespace from a CSV import.
 *   The stray character is a different string, so it slips past the unique index —
 *   which is how the duplicate got created rather than rejected (#374).
 * `cross_association_address` — one street address under two different associations,
 *   usually a lot filed under the wrong HOA.
 */
export type DuplicateAccountKind = 'shortcode' | 'mangled_number' | 'cross_association_address';

export interface DuplicateAccountMember {
	id: string;
	associationId: string;
	associationName: string | null;
	accountNumber: string | null;
	address: string | null;
	status: string | null;
	archivedAt: number | null;
	ownerName: string | null;
	/** Attachment counts decide whether a duplicate can be removed outright or needs
	 *  its rows repointed first. */
	chargeCount: number;
	paymentCount: number;
	applicationCount: number;
}

export interface DuplicateAccountGroup {
	kind: DuplicateAccountKind;
	/** The shared value that grouped them. */
	key: string;
	members: DuplicateAccountMember[];
}

/**
 * An `ExteriorApplication` enriched with the read-only display fields the API
 * folds in (status label + joined association/account/request-type names). Every
 * `GET /exterior-applications` response — list and single — carries these, so the
 * client works with this shape end-to-end.
 */
export interface ExteriorApplicationWithMeta extends ExteriorApplication {
	/** Calculated label: "<account label>: YYYY-MM-DD - <type>". Server-computed via
	 *  `applicationDisplayTitle` so UI, exports and document folder names agree. */
	label?: string;
	statusLabel?: string;
	associationName?: string;
	accountAddress?: string;
	accountNumber?: string;
	accountOwnerName?: string;
	requestTypeName?: string;
	requestTypeSlug?: string;
	/** Which side owes the next move. Derived from the newest message + status, so it
	 *  can never disagree with the thread — see `utils/application-waiting.ts`. */
	waitingSide?: WaitingSide;
	/** Messages in the thread nobody here has opened yet. */
	unreadCount?: number;
}

/**
 * A unit a complaint is AGAINST (#566). The complaint itself stays filed under whoever
 * sent it (`messages.account_id`); these are the lots it is about.
 */
export interface ComplaintTarget {
	accountId: string;
	accountNumber: string | null;
	ownerName: string | null;
	address: string | null;
	addedAt: string | undefined;
}

/** One complaint as the report and the lot page render it. */
export interface ComplaintSummary {
	id: string;
	subject: string | null;
	excerpt: string | null;
	complainantName: string | null;
	complainantAccountId: string | null;
	complainantAccountNumber: string | null;
	status: string;
	/** Business date (midnight-UTC marker of the ET day) the complaint arrived. */
	receivedOn: number;
	/** Business date it was reported to the board, or null. */
	reportedToBoardOn: number | null;
	against: ComplaintTarget[];
}

/** An improvement request waiting on a board decision, as the board-prep report lists it. */
export interface BoardPrepApplication {
	id: string;
	applicationNumber: number | null;
	requestTypeName: string | null;
	submitterName: string | null;
	accountId: string | null;
	accountNumber: string | null;
	accountOwnerName: string | null;
	propertyAddress: string | null;
	description: string | null;
	/** Business date (ET day) the request arrived. */
	receivedOn: number;
	/** Business date already set for the board meeting, when one has been. */
	boardMeetingDate: number | null;
}

/** `GET /board-prep/:associationId` (#566). */
export interface BoardPrepReport {
	associationId: string;
	associationName: string;
	/** Business date the report was generated for — ET "today". */
	generatedOn: number;
	applications: BoardPrepApplication[];
	complaints: {
		open: ComplaintSummary[];
		reportedStillOpen: ComplaintSummary[];
		addressedUnreported: ComplaintSummary[];
	};
}
